import type { components } from '@/api/schema';

import { keysOf, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

export type InvoiceStatus = Schemas['InvoiceView']['status'];
export type PaymentMethod = Schemas['PaymentView']['method'];
export type PayrollStatus = Schemas['PayrollPeriodView']['status'];
export type PriceTierName = Schemas['PriceTier']['tierName'];
export type UnitOfMeasure = Schemas['ServiceItem']['unitOfMeasure'];
export type RebateSponsor = Schemas['Rebate']['sponsor'];
export type CatalogItemType = Schemas['PriceTier']['itemType'];
export type StockMovementReason = Schemas['StockMovement']['reason'];
export type StockReferenceType = Schemas['StockDocumentReference']['type'];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  VOID: 'Void',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CARD: 'Card',
  ACH: 'Bank transfer (ACH)',
  CHECK: 'Check',
  CASH: 'Cash',
  FINANCING: 'Financing',
  OTHER: 'Other',
};

export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = {
  OPEN: 'Open',
  LOCKED: 'Locked',
};

export const PRICE_TIER_LABELS: Record<PriceTierName, string> = {
  STANDARD: 'Standard',
  MEMBER: 'Member',
  AFTER_HOURS: 'After hours',
  NEW_CONSTRUCTION: 'New construction',
  PROMOTIONAL: 'Promotional',
};

// A unit is spoken two ways: as the basis a price is quoted on ("Per hour") and as the noun that
// follows a quantity ("5 hours"). They are one enum with two phrasings, not two vocabularies.
// "Visit" is the sales word, so a flat charge for one attendance reads as a job.
export const UNIT_PRICE_LABELS: Record<UnitOfMeasure, string> = {
  EACH: 'Each',
  HOUR: 'Per hour',
  VISIT: 'Per job',
  DAY: 'Per day',
  FOOT: 'Per foot',
  POUND: 'Per pound',
};

export const UNIT_QUANTITY_LABELS: Record<UnitOfMeasure, string> = {
  EACH: 'each',
  HOUR: 'hours',
  VISIT: 'jobs',
  DAY: 'days',
  FOOT: 'feet',
  POUND: 'pounds',
};

export const SPONSOR_LABELS: Record<RebateSponsor, string> = {
  MANUFACTURER: 'Manufacturer',
  UTILITY: 'Utility',
  GOVERNMENT: 'Government',
  DISTRIBUTOR: 'Distributor',
  IN_HOUSE: 'In house',
};

export const CATALOG_ITEM_TYPE_LABELS: Record<CatalogItemType, string> = {
  SERVICE: 'Service',
  EQUIPMENT: 'Equipment',
};

/**
 * Whether a price's dates put it in effect today. The screen works this out; the server never
 * sends it.
 */
export type PriceWindowState = 'live' | 'future' | 'expired' | 'unknown';

export const PRICE_WINDOW_LABELS: Record<PriceWindowState, string> = {
  live: 'In effect',
  future: 'Scheduled',
  expired: 'Expired',
  unknown: 'Dates not checked',
};

export const STOCK_REASON_LABELS: Record<StockMovementReason, string> = {
  RECEIPT: 'Received',
  RETURN: 'Returned',
  JOB_RESERVE: 'Reserved for a job',
  ADJUSTMENT: 'Adjustment',
  INVOICE_DEDUCT: 'Used on an invoice',
};

export const STOCK_REFERENCE_LABELS: Record<StockReferenceType, string> = {
  INVOICE: 'Invoice',
  JOB: 'Job',
  PURCHASE_ORDER: 'Purchase order',
  COUNT: 'Stock count',
  IMPORT: 'Import',
};

/** The price tiers in the order the price timeline shows them. */
export const PRICE_TIERS = keysOf(PRICE_TIER_LABELS);

/** The invoice statuses as `{ value, label }`, for a filter. */
export const INVOICE_STATUS_OPTIONS = optionsOf(INVOICE_STATUS_LABELS);

/** The ways a payment can arrive as `{ value, label }`, for a select. */
export const PAYMENT_METHOD_OPTIONS = optionsOf(PAYMENT_METHOD_LABELS);

/** The price bases as `{ value, label }`, for a select. */
export const UNIT_PRICE_OPTIONS = optionsOf(UNIT_PRICE_LABELS);

/** The rebate sponsors as `{ value, label }`, for a select. */
export const REBATE_SPONSOR_OPTIONS = optionsOf(SPONSOR_LABELS);

export function invoiceStatusLabel(status: string | null | undefined): string {
  return labelOf(INVOICE_STATUS_LABELS, status);
}

export function paymentMethodLabel(method: string | null | undefined): string {
  return labelOf(PAYMENT_METHOD_LABELS, method);
}

export function payrollStatusLabel(status: string | null | undefined): string {
  return labelOf(PAYROLL_STATUS_LABELS, status);
}

export function priceTierLabel(tier: string | null | undefined): string {
  return labelOf(PRICE_TIER_LABELS, tier);
}

export function unitPriceLabel(unit: string | null | undefined): string {
  return labelOf(UNIT_PRICE_LABELS, unit);
}

export function unitQuantityLabel(unit: string | null | undefined): string {
  return labelOf(UNIT_QUANTITY_LABELS, unit);
}

export function sponsorLabel(sponsor: string | null | undefined): string {
  return labelOf(SPONSOR_LABELS, sponsor);
}

export function catalogItemTypeLabel(type: string | null | undefined): string {
  return labelOf(CATALOG_ITEM_TYPE_LABELS, type);
}

export function stockReasonLabel(reason: string | null | undefined): string {
  return labelOf(STOCK_REASON_LABELS, reason);
}

export function stockReferenceLabel(type: string | null | undefined): string {
  return labelOf(STOCK_REFERENCE_LABELS, type);
}

export function priceWindowLabel(state: string | null | undefined): string {
  return labelOf(PRICE_WINDOW_LABELS, state);
}
